import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { showToast } from "@/lib/toast";
import { fetchWithAuth } from "@/lib/api/client";

// ============================================================================
// Types
// ============================================================================

export interface Agent {
  name: string;
  display_name: string;
  description: string;
  category: string;
}

export interface AgentExecution {
  id: string;
  agent_name: string;
  agent_display_name?: string;
  licitacion_id: string;
  status?: "running" | "completed" | "failed";
  result: any;
  sources?: string[];
  confidence?: number;
  confidence_score?: number;
  execution_time_ms?: number;
  error?: string;
  created_at: string;
  updated_at?: string;
}

export interface ExecuteAgentParams {
  licitacion_id: string;
  agent_name: string;
  parameters?: Record<string, any>;
}

// ============================================================================
// API Functions
// ============================================================================

async function fetchAgentsList(): Promise<Agent[]> {
  const data = await fetchWithAuth("/api/v1/agents");
  return data.agents || data || [];
}

async function executeAgent(params: ExecuteAgentParams): Promise<AgentExecution> {
  const { agent_name, licitacion_id, parameters } = params;

  return fetchWithAuth(`/api/v1/agents/${agent_name}/execute`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      licitacion_id,
      input_data: parameters || {},
    }),
  });
}

async function fetchAgentExecutions(licitacionId: string): Promise<AgentExecution[]> {
  const data = await fetchWithAuth(
    `/api/v1/agents/executions?licitacion_id=${licitacionId}`
  );
  return data.items || data.executions || [];
}

async function fetchAgentExecution(executionId: string): Promise<AgentExecution> {
  return fetchWithAuth(`/api/v1/agents/executions/${executionId}`);
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook para obtener la lista de agentes disponibles
 */
export function useAgentsList() {
  return useQuery<Agent[], Error>({
    queryKey: ["agents"],
    queryFn: fetchAgentsList,
    staleTime: 5 * 60 * 1000, // 5 minutos
  });
}

/**
 * Hook para ejecutar un agente
 * @param agentName - Nombre del agente a ejecutar
 */
export function useExecuteAgent(agentName: string) {
  const queryClient = useQueryClient();

  return useMutation<AgentExecution, Error, Omit<ExecuteAgentParams, "agent_name">>({
    mutationFn: (params) =>
      executeAgent({
        ...params,
        agent_name: agentName,
      }),
    onSuccess: (data, variables) => {
      // Invalidar la lista de ejecuciones para la licitación
      queryClient.invalidateQueries({
        queryKey: ["agent-executions", variables.licitacion_id],
      });
      showToast.success("Agente ejecutado correctamente");
    },
    onError: () => {
      showToast.error("Error al ejecutar el agente");
    },
  });
}

/**
 * Hook para obtener las ejecuciones de agentes de una licitación
 * @param licitacionId - ID de la licitación
 */
export function useAgentExecutions(licitacionId: string | null) {
  return useQuery<AgentExecution[], Error>({
    queryKey: ["agent-executions", licitacionId],
    queryFn: () => fetchAgentExecutions(licitacionId!),
    enabled: !!licitacionId,
    refetchInterval: (query) => {
      // Refetch cada 3 segundos si hay ejecuciones en estado "running"
      const hasRunning = query.state.data?.some((exec) => exec.status === "running");
      return hasRunning ? 3000 : false;
    },
  });
}

/**
 * Hook para obtener el detalle de una ejecución específica
 * @param executionId - ID de la ejecución
 */
export function useAgentExecution(executionId: string | null) {
  return useQuery<AgentExecution, Error>({
    queryKey: ["agent-execution", executionId],
    queryFn: () => fetchAgentExecution(executionId!),
    enabled: !!executionId,
    refetchInterval: (query) => {
      // Refetch cada 3 segundos si la ejecución está en estado "running"
      return query.state.data?.status === "running" ? 3000 : false;
    },
  });
}
